import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView    } from 'framer-motion';
import { RiPlantLine, RiEarthLine, RiGroupLine, RiBookOpenLine, RiArrowRightLine } from 'react-icons/ri';

const StatCard = ({ icon: Icon, value, label, suffix = "", index, className = "" }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    useEffect(() => {
        if (isInView) {
            let start = 0;
            const end = parseInt(value);
            if (start === end) return;
            const duration = 2000;
            const increment = end / (duration / 16);
            const timer = setInterval(() => {
                start += increment;
                if (start >= end) {
                    setCount(end);
                    clearInterval(timer);
                } else {
                    setCount(Math.floor(start));
                }
            }, 16);
            return () => clearInterval(timer);
        }
    }, [isInView, value]);

    // Unique organic shapes based on index
    const shapes = [
        "rounded-[60px_20px_60px_20px]",
        "rounded-[20px_60px_20px_60px]",
        "rounded-[40px_40px_10px_40px]",
        "rounded-[10px_40px_40px_40px]"
    ];

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, scale: 0.8, rotate: index % 2 === 0 ? -2 : 2 }}
            animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
            transition={{ duration: 0.8, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
            className={`relative group p-6 ${shapes[index % shapes.length]} backdrop-blur-md border border-white/40 shadow-xl overflow-hidden bg-white/70 hover:bg-white/90 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-900/10 ${className}`}
        >
            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center mb-3 shadow-lg shadow-emerald-900/10 group-hover:rotate-[360deg] transition-transform duration-1000">
                    <Icon className="text-lg text-white" />
                </div>

                <div className="flex items-baseline mb-1">
                    <span className="text-2xl lg:text-3xl font-bold text-slate-900 font-display tracking-tight">
                        {count.toLocaleString()}
                    </span>
                    <span className="text-lg font-bold text-emerald-600 ml-0.5">{suffix}</span>
                </div>

                <div className="text-slate-500 font-bold uppercase tracking-[0.2em] text-[8px]">
                    {label}
                </div>
            </div>
        </motion.div>
    );
};

const ImpactOverview = () => {
    const stats = [
        { icon: RiPlantLine, value: "1500", label: "Trees Planted", suffix: "+" },
        { icon: RiEarthLine, value: "45", label: "Regions Protected", suffix: "" },
        { icon: RiGroupLine, value: "120", label: "Active Volunteers", suffix: "k" },
        { icon: RiBookOpenLine, value: "250", label: "Research Papers", suffix: "+" }
    ];

    return (
        <section className="relative py-20 overflow-hidden bg-[#F9FAF8]">
            <div className="absolute top-0 right-0 w-[40%] h-[100%] bg-gradient-to-l from-emerald-50/30 to-transparent pointer-events-none" />

            <div className="mx-auto max-w-7xl px-6 relative z-10">
                <div className="flex flex-col lg:flex-row gap-16 items-center">

                    {/* Left: Content */}
                    <div className="w-full lg:w-5/12 text-center lg:text-left">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="inline-flex items-center space-x-2 px-3 py-1 bg-emerald-100 rounded-full text-emerald-700 font-bold text-[9px] uppercase tracking-widest mb-4">
                                <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
                                <span>Impact Record</span>
                            </span>

                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-display leading-[1.2] sm:leading-tight">
                                Real Results for a <span className="text-emerald-600 italic">Living</span> World.
                            </h2>

                            <p className="text-sm sm:text-base text-slate-500 mb-8 leading-relaxed font-sans max-w-lg mx-auto lg:mx-0">
                                Our data reflects a global commitment to ecological restoration. We track every milestone to ensure transparency and inspire continued action for biodiversity.
                            </p>

                            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center lg:justify-start">
                                <button className="group flex items-center justify-center space-x-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-900/10 hover:-translate-y-0.5">
                                    <span>Join Mission</span>
                                    <RiArrowRightLine className="group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="px-6 py-3 bg-white text-slate-600 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition-all hover:-translate-y-0.5">
                                    Our Strategy
                                </button>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: Unique Clustered Stats */}
                    <div className="w-full lg:w-7/12 relative py-12">
                        {/* Decorative background circle */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-emerald-100/20 rounded-full blur-[100px] -z-10" />

                        <div className="relative grid grid-cols-2 gap-4 sm:gap-6 max-w-md mx-auto">
                            {/* Card 1: Top Left */}
                            <StatCard {...stats[0]} index={0} className="w-full aspect-square" />

                            {/* Card 2: Top Right - Shifted down on tablet+, reset on mobile if needed */}
                            <StatCard {...stats[1]} index={1} className="w-full aspect-square sm:mt-12" />

                            {/* Card 3: Bottom Left - Shifted up on tablet+ */}
                            <StatCard {...stats[2]} index={2} className="w-full aspect-square sm:-mt-12" />

                            {/* Card 4: Bottom Right */}
                            <StatCard {...stats[3]} index={3} className="w-full aspect-square" />

                            {/* Center Decorative Marker */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-emerald-600/10 backdrop-blur-xl rounded-full border border-emerald-600/20 items-center justify-center z-20 hidden sm:flex">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default ImpactOverview;
